/**
 * Client-side validation (FR-REG-05, FR-REG-11) and mapping of server errors onto form fields.
 * Field keys match the server's: `email`, `first_name`, `last_name`, `phone`, `custom.<key>`.
 */
import type { PublicFormConfig } from '@ffs/shared';
import type { ApiError } from './api';

/** Same syntax rule as the server (`apps/worker/src/lib/email-check.ts`). */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const isValidEmail = (email: string): boolean => EMAIL_RE.test(email.trim());

export interface FormValues {
  first_name: string;
  email: string;
  last_name: string;
  phone: string;
  sms_consent: boolean;
  custom: Record<string, string | boolean>;
}

export type FieldErrors = Record<string, string>;

export type FormRules = Pick<PublicFormConfig, 'last_name' | 'phone' | 'sms_consent' | 'fields'>;

export const emptyValues = (prefill: Partial<FormValues> = {}): FormValues => ({
  first_name: '',
  email: '',
  last_name: '',
  phone: '',
  sms_consent: false,
  custom: {},
  ...prefill,
});

export const SLOT_KEY = 'slot';

/** The email check on its own, for the first step of a two-step form. */
export function emailError(value: string): string | null {
  const email = value.trim();
  if (!email) return 'Enter your email address';
  return isValidEmail(email) ? null : 'Enter a valid email address';
}

/** Returns one message per invalid field; an empty object means the form may be submitted. */
export function validate(
  values: FormValues,
  rules: FormRules,
  hasSlot: boolean,
  slotsOffered = true,
): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.first_name.trim()) errors.first_name = 'Enter your first name';
  const emailErr = emailError(values.email);
  if (emailErr) errors.email = emailErr;
  if (rules.last_name === 'required' && !values.last_name.trim())
    errors.last_name = 'Enter your last name';
  if (rules.phone === 'required' && !values.phone.trim()) errors.phone = 'Enter your phone number';
  if (rules.sms_consent === 'required' && rules.phone !== 'off' && !values.sms_consent)
    errors.sms_consent = 'Please agree to receive text message reminders';
  for (const f of rules.fields) {
    const v = values.custom[f.key];
    const key = `custom.${f.key}`;
    if (f.required && (v === undefined || v === '' || v === false)) {
      errors[key] = f.type === 'checkbox' ? 'This box must be checked' : `${f.label} is required`;
    } else if (f.type === 'select' && typeof v === 'string' && v && !f.options?.includes(v)) {
      errors[key] = 'Choose one of the listed options';
    }
  }
  if (!hasSlot) errors[SLOT_KEY] = slotsOffered ? 'Choose a time' : 'No times are available';
  return errors;
}

/** Builds the `custom` payload: trimmed strings, booleans as-is, empty optional values dropped. */
export function customPayload(
  values: FormValues,
  fields: FormRules['fields'],
): Record<string, string | boolean> | undefined {
  const out: Record<string, string | boolean> = {};
  for (const f of fields) {
    const v = values.custom[f.key];
    if (f.type === 'checkbox') out[f.key] = v === true;
    else if (typeof v === 'string' && v.trim()) out[f.key] = v.trim();
  }
  return Object.keys(out).length ? out : undefined;
}

export type ErrorAction = 'refresh_slots' | 'reset_turnstile' | 'inactive' | 'retry' | null;

export interface MappedError {
  /** Per-field messages (server field keys). */
  fields: FieldErrors;
  /** Form-level message for the aria-live region, or null when the field errors say it all. */
  message: string | null;
  action: ErrorAction;
}

const MESSAGES: Record<string, string> = {
  network:
    'We could not complete your registration. Check your details and connection, then try again.',
  rate_limited: 'Too many attempts. Please wait a few minutes and try again.',
  turnstile_required: 'Verification did not complete. Please try again.',
  turnstile_failed: 'Verification failed. Please try again.',
  slot_unavailable: 'That time is no longer available. Please choose another.',
  no_jit_slot: 'That time is no longer available. Please choose another.',
  not_found: 'This registration form could not be found.',
  invalid_json: 'Something went wrong sending your details. Please try again.',
  http_error: 'Something went wrong. Please try again.',
};

/** Maps an `ApiError` from `POST /api/register` (or reschedule) to field errors + a status message. */
export function mapServerError(err: ApiError): MappedError {
  const fields: FieldErrors = {};
  let general: string | null = null;
  for (const [key, msgs] of Object.entries(err.fields ?? {})) {
    const text = msgs.filter(Boolean).join(', ') || 'Invalid';
    if (key === 'turnstile_token' || key === '_' || key === 'form_id' || key === 'attribution')
      general = general ?? MESSAGES.turnstile_failed!;
    else if (key === 'slot_start_at') fields[SLOT_KEY] = MESSAGES.slot_unavailable!;
    else fields[key] = humanize(key, text);
  }
  switch (err.code) {
    case 'webinar_inactive':
      return { fields, message: err.message, action: 'inactive' };
    case 'slot_unavailable':
    case 'no_jit_slot':
      return {
        fields: { [SLOT_KEY]: MESSAGES[err.code]! },
        message: null,
        action: 'refresh_slots',
      };
    case 'turnstile_required':
    case 'turnstile_failed':
      return { fields: {}, message: MESSAGES[err.code]!, action: 'reset_turnstile' };
    case 'invalid_email':
    case 'disposable_email':
      return { fields: { email: fields.email ?? err.message }, message: null, action: null };
    case 'banned':
    case 'already_started':
    case 'no_form':
      return { fields, message: err.message, action: null };
    case 'validation_failed':
      return {
        fields,
        message: Object.keys(fields).length ? null : (general ?? err.message),
        action: general ? 'reset_turnstile' : null,
      };
    default:
      if (err.status === 429) return { fields, message: MESSAGES.rate_limited!, action: null };
      if (err.status >= 500) return { fields, message: MESSAGES.http_error!, action: 'retry' };
      return {
        fields,
        message: MESSAGES[err.code] ?? err.message ?? MESSAGES.http_error!,
        action: null,
      };
  }
}

function humanize(key: string, text: string): string {
  if (text === 'Required') {
    if (key === 'last_name') return 'Enter your last name';
    if (key === 'phone') return 'Enter your phone number';
    if (key === 'first_name') return 'Enter your first name';
    if (key === 'email') return 'Enter your email address';
    if (key === 'sms_consent') return 'Please agree to receive text message reminders';
    return 'This field is required';
  }
  if (text === 'Invalid' && key === 'email') return 'Enter a valid email address';
  return text;
}
